import { defineMeta } from '@/registry/types';

export const entityMenuMeta = defineMeta({
  name: 'EntityMenu',
  level: 'molecule',
  category: 'navigation',
  classification: 'custom',
  description:
    'The detail and pivot menu opened by an entity Chip. Three fixed regions -- identity, attributes, actions -- with built-in copy and defanged-copy actions that always yield the canonical value.',
  baseLibrary: '@mui/material',
  muiEquivalent: 'Menu (composed)',
  muiDocs: 'https://mui.com/material-ui/react-menu/',
  importPath: '@/molecules/EntityMenu',
  requiredProps: ['entity', 'anchorEl', 'open', 'onClose'],
  optionalProps: ['actions', 'onAction', 'disableCopyFeedback'],
  supportedStates: ['closed', 'open', 'action-unavailable', 'copied', 'copy-failed'],
  designTokens: ['typography', 'spacing'],
  accessibility: [
    'The menu list is named with the kind and value, so a user who opened one of several chips in a paragraph knows which.',
    'Escape closes the menu and returns focus to the chip that opened it, which MUI Menu handles.',
    'Unavailable actions stay focusable and state their reason as text, so keyboard and screen-reader users can find out why rather than retrying.',
  ],
  usageExamples: [
    '<EntityMenu entity={{ kind: "ip", value: "203.0.113.47", verdict: "malicious" }} anchorEl={anchor} open={Boolean(anchor)} onClose={close} />',
    '<EntityMenu entity={entity} anchorEl={anchor} open={open} onClose={close} actions={[{ id: "pivot", label: "Pivot to search" }]} onAction={run} />',
  ],
  rules: [
    'Open it from a Chip with purpose="entity"; pass the click event\'s currentTarget as anchorEl.',
    'Pass the canonical full value as `entity.value` and the shortened form as `entity.display`. Copy actions read `value`, so a truncated hash still copies in full.',
    'Copy value and Copy defanged are built in. Defanged copy appears only for network kinds (ip, domain, url, email), where it is meaningful.',
    'Keep `actions` to about three: pivot to search, add to current filter, open entity page.',
    'Put a reason on any action the user cannot run; never pass a bare disabled action.',
    'Give attributes short labels and let the menu lay them out; two to five is the useful range.',
  ],
  antiPatterns: [
    'Do not put destructive or privileged response actions here (isolate host, disable account). This menu opens from a word in a paragraph and is far too easy to trigger by accident; those belong on the entity page behind confirmation and permission checks.',
    'Do not pass a truncated display string as `entity.value`; the copy actions would then yield the wrong thing.',
    'Do not attach a verdict to a value that was never resolved. Omit it, which reads as "we did not look this up" rather than as "unknown".',
    'Do not reimplement the copy actions at the call site; they exist here so the canonical-value and defanging rules hold everywhere.',
  ],
  mountsSurface: 'portal',
  llmSafe: true,
  whenToUse: [
    'An analyst needs to copy, look up, or pivot on an entity value shown inline',
    'Showing verdict and enrichment attributes for an indicator without leaving the page',
  ],
  whenNotToUse: [
    'Response actions that change state (use the entity page)',
    'A generic action menu on a row or toolbar (use Menu)',
    'Non-interactive entity values in card copy (use CardDataTag)',
  ],
});

export default entityMenuMeta;
